import React, { useState, useCallback } from 'react'
import { Box, IconButton, List, ListItem, ListItemButton, ListItemContent, ListItemDecorator, Sheet, Typography } from '@mui/joy'
import { Schedule, Settings, Logout, AdminPanelSettings, People, CalendarToday, SwapHoriz, Notifications, Note, Favorite, LocalOffer, Person, AccountTree, Menu as MenuIcon, Close } from '@mui/icons-material'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import useMediaQuery from '@mui/system/useMediaQuery'
import NotificationBadge from './NotificationBadge'

const SIDEBAR_KEY = 'sidebarOpen'
const SIDEBAR_WIDTH = 240

function getInitialSidebarOpen(isLargeScreen: boolean): boolean {
  const stored = localStorage.getItem(SIDEBAR_KEY)
  if (stored !== null) return stored === 'true'
  return isLargeScreen
}

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, permissions } = useAuth()
  const isLargeScreen = useMediaQuery('(min-width:1200px)')

  const [sidebarOpen, setSidebarOpenRaw] = useState(() => getInitialSidebarOpen(isLargeScreen))

  const setSidebarOpen = useCallback((open: boolean) => {
    setSidebarOpenRaw(open)
    localStorage.setItem(SIDEBAR_KEY, String(open))
  }, [])

  const hasPermission = (feature: string, action: string) => {
    const permissionName = `${feature}.${action}`
    return permissions.includes(permissionName)
  }

  const navigationItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: <NotificationBadge />,
      requiredPermission: 'dashboard.view'
    },
    { 
      path: '/users', 
      label: 'Users', 
      icon: <People />,
      requiredPermission: 'users.view',
      excludeRoles: ['employee']
    },
    { 
      path: '/shift-types', 
      label: 'Shift Types', 
      icon: <Settings />,
      requiredPermission: 'shift_types.view'
    },
    { 
      path: '/schedule', 
      label: 'Schedule', 
      icon: <Schedule />,
      requiredPermission: 'schedules.view'
    },
    { 
      path: '/calendar', 
      label: 'Calendar', 
      icon: <CalendarToday />,
      requiredPermission: 'schedules.view'
    },
    { 
      path: '/change-requests', 
      label: 'Change Requests', 
      icon: <SwapHoriz />,
      requiredPermission: 'change_requests.view'
    },
    { 
      path: '/notifications', 
      label: 'Notifications', 
      icon: <Notifications />,
      requiredPermission: 'notifications.view'
    },
    { 
      path: '/notes', 
      label: 'Notes', 
      icon: <Note />,
      requiredPermission: 'notes.view'
    },
    { 
      path: '/preferences', 
      label: 'Preferences', 
      icon: <Favorite />,
      requiredPermission: 'preferences.view'
    },
    { 
      path: '/all-preferences', 
      label: 'All Preferences', 
      icon: <People />,
      requiredPermission: 'preferences.view_all'
    },
    { 
      path: '/tags', 
      label: 'Tags', 
      icon: <LocalOffer />,
      requiredPermission: 'tags.view'
    },
    { 
      path: '/contacts', 
      label: 'Contacts', 
      icon: <Person />,
      requiredPermission: 'contacts.view'
    },
    { 
      path: '/projects', 
      label: 'Projects', 
      icon: <AccountTree />,
      requiredPermission: 'projects.view'
    },
    { 
      path: '/contact-lists', 
      label: 'Contact Lists', 
      icon: <People />,
      requiredPermission: 'contact_lists.view'
    },
    { 
      path: '/admin/permissions', 
      label: 'Permissions', 
      icon: <AdminPanelSettings />,
      requiredPermission: 'users.manage_roles',
      adminOnly: true
    },
  ]

  const drawerContent = (
    <>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography level="h4" fontWeight="lg">
            Shiftifi
          </Typography>
          {user && (
            <Typography level="body-sm" color="neutral">
              {user.first_name} {user.last_name}
            </Typography>
          )}
          {permissions.length > 0 && (
            <Typography level="body-xs" color="neutral">
              Permissions: {permissions.length}
            </Typography>
          )}
        </Box>
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          onClick={() => setSidebarOpen(false)}
        >
          <Close />
        </IconButton>
      </Box>

      <List size="sm" sx={{ '--ListItem-radius': '8px', '--List-gap': '4px', overflow: 'auto', flexGrow: 1 }}>
        {navigationItems.map((item) => {
          const hasAccess = !item.requiredPermission || hasPermission(
            item.requiredPermission.split('.')[0], 
            item.requiredPermission.split('.')[1]
          )
          const isAdminOnly = item.adminOnly && user?.role === 'admin'
          const isExcluded = item.excludeRoles && user?.role && item.excludeRoles.includes(user.role)
          const showItem = hasAccess && (!item.adminOnly || isAdminOnly) && !isExcluded
          
          if (!showItem) return null
          
          return (
            <ListItem key={item.path}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{ borderRadius: '8px' }}
              >
                <ListItemDecorator>{item.icon}</ListItemDecorator>
                <ListItemContent>{item.label}</ListItemContent>
              </ListItemButton>
            </ListItem>
          )
        })}
        
        <ListItem>
          <ListItemButton
            onClick={() => {
              logout();
              navigate('/login');
            }}
            sx={{ borderRadius: '8px', color: 'danger.500' }}
          >
            <ListItemDecorator>
              <Logout />
            </ListItemDecorator>
            <ListItemContent>Logout</ListItemContent>
          </ListItemButton>
        </ListItem>
      </List>
    </>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {!sidebarOpen && (
        <IconButton
          onClick={() => setSidebarOpen(true)}
          variant="outlined"
          color="neutral"
          size="sm"
          sx={{ position: 'fixed', top: 16, left: 16, zIndex: 1100 }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Sheet
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: SIDEBAR_WIDTH,
          transform: sidebarOpen ? 'translateX(0)' : `translateX(-${SIDEBAR_WIDTH}px)`,
          transition: 'transform 0.3s',
          zIndex: 1100,
          borderRight: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.surface',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {drawerContent}
      </Sheet>

      <Box
        sx={{
          flex: 1,
          backgroundColor: 'background.level1',
          ml: sidebarOpen ? `${SIDEBAR_WIDTH}px` : 0,
          transition: 'margin-left 0.3s',
        }}
      >
        <Box sx={{ p: 3, pl: sidebarOpen ? 3 : 8, transition: 'padding-left 0.3s' }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default Layout